(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-sncol-root]';

  function number(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function all(root, selector) {
    return [...root.querySelectorAll(selector)];
  }

  function initPriceFilter(scope) {
    all(scope, '[data-sncol-price]').forEach((price) => {
      if (price.dataset.sncolPriceReady === 'true') return;
      price.dataset.sncolPriceReady = 'true';

      const rangeMin = price.querySelector('[data-sncol-range-min]');
      const rangeMax = price.querySelector('[data-sncol-range-max]');
      const fieldMin = price.querySelector('[data-sncol-price-min]');
      const fieldMax = price.querySelector('[data-sncol-price-max]');
      const track = price.querySelector('[data-sncol-range-track]');
      const maxAllowed = Math.max(0, number(price.dataset.max));

      if (!rangeMin || !rangeMax || !fieldMin || !fieldMax || !track || !maxAllowed) {
        return;
      }

      const clamp = (value) => Math.max(0, Math.min(maxAllowed, number(value)));

      function sync(source) {
        let min = clamp(source === fieldMin ? fieldMin.value : rangeMin.value);
        let max = clamp(source === fieldMax ? fieldMax.value : rangeMax.value);

        if (min > max) {
          if (source === rangeMin || source === fieldMin) min = max;
          else max = min;
        }

        rangeMin.value = String(min);
        rangeMax.value = String(max);
        fieldMin.value = String(Math.round(min));
        fieldMax.value = String(Math.round(max));

        track.style.setProperty('--sncol-min-pct', `${(min / maxAllowed) * 100}%`);
        track.style.setProperty('--sncol-max-pct', `${(max / maxAllowed) * 100}%`);
      }

      [rangeMin, rangeMax].forEach((input) => {
        input.addEventListener('input', () => sync(input));
      });

      [fieldMin, fieldMax].forEach((input) => {
        input.addEventListener('change', () => sync(input));
      });

      sync(rangeMin);
    });
  }

  function initCollection(section) {
    if (!section || section.dataset.sncolReady === 'true') return;
    section.dataset.sncolReady = 'true';

    const sectionId = section.dataset.sectionId;
    const total = Math.max(0, number(section.dataset.total));
    const grid = section.querySelector('[data-sncol-grid]');
    const pager = section.querySelector('[data-sncol-pagination]');
    const loadMore = section.querySelector('[data-sncol-load-more]');
    const status = section.querySelector('[data-sncol-status]');
    const progress = section.querySelector('[data-sncol-progress]');
    const pageSizeSelect = section.querySelector('[data-sncol-page-size]');

    let batch = Math.max(1, number(pageSizeSelect?.value || section.dataset.batch, 20));
    let nextUrl = pager?.dataset.nextUrl || '';
    let fetching = false;

    if (!grid) return;

    const items = () => all(grid, '[data-sncol-product-item]');
    const visibleItems = () => items().filter((item) => !item.hidden);

    function updateStatus() {
      const visible = Math.min(visibleItems().length, total);
      if (status) {
        status.textContent = total > 0
          ? `Showing 1–${visible} of ${total} total`
          : 'Showing 0 of 0 total';
      }

      if (progress) {
        const percent = total > 0 ? Math.min(100, (visible / total) * 100) : 0;
        progress.style.width = `${percent}%`;
      }

      if (!loadMore) return;

      const done = visible >= total || (!nextUrl && visible >= items().length);

      loadMore.disabled = done || fetching;
      loadMore.textContent = done
        ? 'NO MORE PRODUCT'
        : fetching
          ? 'LOADING...'
          : 'SHOW MORE';

      loadMore.classList.toggle('is-loading', fetching);
    }

    function revealThrough(target) {
      items().forEach((item, index) => {
        item.hidden = index >= target;
      });
      updateStatus();
    }

    async function fetchNextPage() {
      if (!nextUrl || fetching) return false;

      fetching = true;
      updateStatus();

      try {
        const url = new URL(nextUrl, window.location.origin);
        url.searchParams.set('section_id', sectionId);

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) {
          throw new Error(`Collection request failed: ${response.status}`);
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incomingRoot = doc.querySelector(ROOT_SELECTOR);
        const incomingGrid = incomingRoot?.querySelector('[data-sncol-grid]');
        const incomingPager = incomingRoot?.querySelector('[data-sncol-pagination]');

        if (!incomingGrid) {
          nextUrl = '';
          return false;
        }

        const fragment = document.createDocumentFragment();

        all(incomingGrid, '[data-sncol-product-item]').forEach((item) => {
          item.hidden = true;
          fragment.appendChild(item);
        });

        grid.appendChild(fragment);
        nextUrl = incomingPager?.dataset.nextUrl || '';

        return true;
      } catch (error) {
        console.error('[Synmac Collection Pro]', error);
        return false;
      } finally {
        fetching = false;
        updateStatus();
      }
    }

    async function ensureLoaded(target) {
      while (items().length < target && nextUrl) {
        const added = await fetchNextPage();
        if (!added) break;
      }
    }

    loadMore?.addEventListener('click', async () => {
      if (fetching || loadMore.disabled) return;

      const current = visibleItems().length;
      const target = Math.min(total, current + batch);

      await ensureLoaded(target);
      revealThrough(Math.min(target, items().length));
    });

    pageSizeSelect?.addEventListener('change', async () => {
      batch = Math.max(1, number(pageSizeSelect.value, 20));
      const target = Math.min(total, batch);

      await ensureLoaded(target);
      revealThrough(Math.min(target, items().length));
    });

    all(section, '[data-sncol-sort]').forEach((select) => {
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.assign(url.toString());
      });
    });

    all(section, '[data-sncol-cols]').forEach((button) => {
      button.addEventListener('click', () => {
        const cols = Math.max(1, number(button.dataset.sncolCols, 1));
        const mode = button.dataset.sncolMode;

        if (mode === 'mobile') {
          section.style.setProperty('--sncol-cols-m', String(Math.min(cols, 2)));
        } else {
          section.style.setProperty('--sncol-cols-d', String(Math.min(cols, 4)));
        }

        all(section, `[data-sncol-cols][data-sncol-mode="${mode}"]`).forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      });
    });

    const shell = section.querySelector('[data-sncol-drawer-shell]');
    const openButton = section.querySelector('[data-sncol-filter-open]');
    const closeButtons = all(section, '[data-sncol-filter-close]');
    const drawer = section.querySelector('[data-sncol-drawer]');

    function openDrawer() {
      if (!shell) return;
      shell.classList.add('is-open');
      shell.setAttribute('aria-hidden', 'false');
      openButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('sncol-lock');

      window.setTimeout(() => {
        const firstFocusable = drawer?.querySelector('button, a, input, select, summary');

        if (drawer && firstFocusable && typeof trapFocus === 'function') {
          trapFocus(drawer, firstFocusable);
        } else {
          firstFocusable?.focus();
        }
      }, 30);
    }

    function closeDrawer() {
      if (!shell) return;
      shell.classList.remove('is-open');
      shell.setAttribute('aria-hidden', 'true');
      openButton?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('sncol-lock');

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(openButton);
      } else {
        openButton?.focus();
      }
    }

    openButton?.addEventListener('click', openDrawer);
    closeButtons.forEach((button) => button.addEventListener('click', closeDrawer));

    const sortShell = section.querySelector('[data-sncol-sort-shell]');
    const sortOpenButton = section.querySelector('[data-sncol-sort-open]');
    const sortCloseButtons = all(section, '[data-sncol-sort-close]');
    const sortSheet = section.querySelector('[data-sncol-sort-sheet]');

    function openSortDrawer() {
      if (!sortShell) return;

      sortShell.classList.add('is-open');
      sortShell.setAttribute('aria-hidden', 'false');
      sortOpenButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('sncol-lock');

      window.setTimeout(() => {
        const firstFocusable = sortSheet?.querySelector('[data-sncol-sort-option].is-active, [data-sncol-sort-option]');

        if (sortSheet && firstFocusable && typeof trapFocus === 'function') {
          trapFocus(sortSheet, firstFocusable);
        } else {
          firstFocusable?.focus();
        }
      }, 30);
    }

    function closeSortDrawer(returnFocus = true) {
      if (!sortShell) return;

      sortShell.classList.remove('is-open');
      sortShell.setAttribute('aria-hidden', 'true');
      sortOpenButton?.setAttribute('aria-expanded', 'false');

      if (!shell?.classList.contains('is-open')) {
        document.body.classList.remove('sncol-lock');
      }

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(returnFocus ? sortOpenButton : null);
      } else if (returnFocus) {
        sortOpenButton?.focus();
      }
    }

    sortOpenButton?.addEventListener('click', openSortDrawer);
    sortCloseButtons.forEach((button) => {
      button.addEventListener('click', () => closeSortDrawer());
    });

    all(section, '[data-sncol-sort-option]').forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.dataset.sncolSortOption;
        if (!value) return;

        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', value);
        url.searchParams.delete('page');
        window.location.assign(url.toString());
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      if (sortShell?.classList.contains('is-open')) {
        closeSortDrawer();
        return;
      }

      if (shell?.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    initPriceFilter(section);
    updateStatus();
  }

  function initAll(root = document) {
    if (root.matches?.(ROOT_SELECTOR)) initCollection(root);
    all(root, ROOT_SELECTOR).forEach(initCollection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();